import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { copyTasksToCustomerAssurance } from "@/services/frameworkService";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTenancy } from "@/contexts/TenancyContext";

interface DISPState {
  enabled: "enabled" | "disabled";
  membershipNumber: string;
  membershipLevel: DISPLevel;
  expiryDate: string;
  primaryContact: string;
  securityGovernance: DISPLevel;
  personnelSecurity: DISPLevel;
  physicalSecurity: DISPLevel;
  informationCyberSecurity: DISPLevel;
  chiefSecurityOfficer: string;
  securityOfficer: string;
}

type DISPLevel = "entryLevel" | "levelOne" | "levelTwo" | "levelThree" | "";

const DISP_LEVEL_OPTIONS = [
  { value: "entryLevel", label: "Entry Level" },
  { value: "levelOne", label: "Level 1" },
  { value: "levelTwo", label: "Level 2" },
  { value: "levelThree", label: "Level 3" },
];

const defaultDISP: DISPState = {
  enabled: "disabled",
  membershipNumber: "",
  membershipLevel: "",
  expiryDate: "",
  primaryContact: "",
  securityGovernance: "",
  personnelSecurity: "",
  physicalSecurity: "",
  informationCyberSecurity: "",
  chiefSecurityOfficer: "",
  securityOfficer: "",
};

export default function DISPModule() {
  const [disp, setDisp] = useState<DISPState>(defaultDISP);
  const [dispInitial, setDispInitial] = useState<DISPState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const {activeTenancy} = useTenancy();

  useEffect(() => {
    if (!activeTenancy || ["", "Loading..."]?.includes(activeTenancy?.id)) {
      return;
    }
    const fetchDISP = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(activeTenancy?.id, "complianceModules");
        const dispData = settings?.["disp"] || {};
        
        // Check if it's the new object-based format
        if (dispData.Enabled !== undefined || dispData.DISPSecurityGovernance !== undefined) {
          // New format: {Enabled: true, DISPSecurityGovernance: "levelOne", ...}
          const state = dispObjectToState(dispData);
          setDisp(state);
          setDispInitial(state);
        } else {
          // Old format: {enabled: true, [array of objects]}
          const dispEnabled = dispData?.enabled ?? false;
          const arr = Array.isArray(dispData) ? dispData : Object.values(dispData).filter(x => typeof x === 'object' && x.settingName);
          const state = dispArrayToState(arr, dispEnabled);
          setDisp(state);
          setDispInitial(state);
        }
      } catch (err) {
        console.error("Failed to fetch DISP settings:", err);
        toast.error("Failed to fetch DISP settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchDISP();
  }, [activeTenancy]);

  // Convert new object-based format to state
  function dispObjectToState(obj: any): DISPState {
    return {
      enabled: obj.Enabled ? "enabled" : "disabled",
      membershipNumber: obj.DISPMembershipNumber || "",
      membershipLevel: obj.DISPMembershipLevel || "",
      expiryDate: obj.DISPExpiryDate || "",
      primaryContact: obj.DISPPrimaryContact || "",
      securityGovernance: obj.DISPSecurityGovernance || "",
      personnelSecurity: obj.DISPPersonnelSecurity || "",
      physicalSecurity: obj.DISPPhysicalSecurity || "",
      informationCyberSecurity: obj.DISPInformationCyberSecurity || "",
      chiefSecurityOfficer: obj.DISPChiefSecurityOfficer || "",
      securityOfficer: obj.DISPSecurityOfficer || "",
    };
  }

  // Convert old array-based format to state
  function dispArrayToState(arr: any[], enabled: boolean): DISPState {
    const get = (name: string) => arr?.find(e => e.settingName === name)?.value || "";
    return {
      enabled: enabled ? "enabled" : "disabled",
      membershipNumber: get("DISP Membership Number"),
      membershipLevel: get("DISP Membership Level"),
      expiryDate: get("DISP Expiry Date"),
      primaryContact: get("Primary Contact"),
      securityGovernance: get("DISP Security Governance"),
      personnelSecurity: get("DISP Personnel Security"),
      physicalSecurity: get("DISP Physical Security"),
      informationCyberSecurity: get("DISP Information Cyber Security"),
      chiefSecurityOfficer: get("Chief Security Officer (CSO)"),
      securityOfficer: get("Security Officer"),
    };
  }

  // Convert state to new object-based format (preferred)
  function dispStateToObject(state: DISPState) {
    return {
      Enabled: state.enabled === "enabled",
      DISPMembershipNumber: state.membershipNumber,
      DISPMembershipLevel: state.membershipLevel,
      DISPExpiryDate: state.expiryDate,
      DISPPrimaryContact: state.primaryContact,
      DISPSecurityGovernance: state.securityGovernance,
      DISPPersonnelSecurity: state.personnelSecurity,
      DISPPhysicalSecurity: state.physicalSecurity,
      DISPInformationCyberSecurity: state.informationCyberSecurity,
      DISPChiefSecurityOfficer: state.chiefSecurityOfficer,
      DISPSecurityOfficer: state.securityOfficer,
    };
  }

  // Convert state to old array-based format (legacy)
  function dispStateToArray(state: DISPState) {
    return [
      { settingName: "DISP Membership Number", value: state.membershipNumber },
      { settingName: "DISP Membership Level", value: state.membershipLevel },
      { settingName: "DISP Expiry Date", value: state.expiryDate },
      { settingName: "Primary Contact", value: state.primaryContact },
      { settingName: "DISP Security Governance", value: state.securityGovernance },
      { settingName: "DISP Personnel Security", value: state.personnelSecurity },
      { settingName: "DISP Physical Security", value: state.physicalSecurity },
      { settingName: "DISP Information Cyber Security", value: state.informationCyberSecurity },
      { settingName: "Chief Security Officer (CSO)", value: state.chiefSecurityOfficer },
      { settingName: "Security Officer", value: state.securityOfficer },
    ];
  }

  const dispChanged = dispInitial && JSON.stringify(disp) !== JSON.stringify(dispInitial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const wasDisabled = dispInitial?.enabled === "disabled";
      const isNowEnabled = disp.enabled === "enabled";
      
      // Save in new object-based format
      const dispObj = dispStateToObject(disp);
      await companyService.saveCompanySettings(activeTenancy?.id, "complianceModules", {
        disp: dispObj,
      });
      
      // If DISP is being enabled (was disabled, now enabled), copy tasks for each category level
      if (wasDisabled && isNowEnabled && activeTenancy?.id) {
        try {
          const customerId = activeTenancy.id;
          
          // Collect all unique levels from the four categories
          const categoryLevels = [
            { category: 'Security Governance', level: disp.securityGovernance },
            { category: 'Personnel Security', level: disp.personnelSecurity },
            { category: 'Physical Security', level: disp.physicalSecurity },
            { category: 'Information/Cyber Security', level: disp.informationCyberSecurity },
          ].filter(cat => cat.level); // Only include categories with a level set
          
          if (categoryLevels.length === 0) {
            toast.success("DISP settings saved");
            setDispInitial({ ...disp });
            return;
          }
          
          // Copy tasks for each category level
          for (const { category, level } of categoryLevels) {
            toast.info(`Copying DISP ${category} (${level}) tasks...`);
            
            // Use matchParams to filter by BOTH category AND level
            await copyTasksToCustomerAssurance(
              customerId,
              'DISP',
              {
                DISPCategory: category,
                DISPLevel: level
              },
              undefined
            );
          }
          
          const levelLabels = categoryLevels.map(c => `${c.category}: ${c.level}`).join(', ');
          toast.success(`DISP settings saved and tasks copied for: ${levelLabels}`);
        } catch (taskError) {
          console.error('Error copying DISP tasks:', taskError);
          toast.warning("Settings saved, but there was an error copying some tasks. Please check the console.");
        }
      } else {
        toast.success("DISP settings saved");
      }
      
      setDispInitial({ ...disp });
    } catch (err) {
      console.error("Failed to save DISP settings:", err);
      toast.error("Failed to save DISP settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-purple-500/10 p-3 rounded-full">
          {/* You can use a relevant icon here */}
          <span role="img" aria-label="DISP">🛡️</span>
        </div>
        <div>
          <div className="font-semibold text-lg">DISP (Defence Industry Security Program)</div>
          <div className="text-xs text-muted-foreground">Australian Government Defence Industry Security Program membership details</div>
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Module Status</label>
          <Select
            value={disp.enabled}
            onValueChange={val => setDisp(v => ({ ...v, enabled: val as DISPState["enabled"] }))}
            disabled={isLoading || isSaving}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="enabled">Enabled</SelectItem>
              <SelectItem value="disabled">Disabled</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* <div>
          <label className="block text-sm font-medium mb-1">Membership Level</label>
          <Select
            value={disp.membershipLevel}
            onValueChange={val => setDisp(v => ({ ...v, membershipLevel: val as DISPLevel }))}
            disabled={isLoading || isSaving}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              {DISP_LEVEL_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div> */}
        <div className="mt-6">
          <label className="block text-sm font-medium mb-1">Chief Security Officer (CSO)</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            value={disp.chiefSecurityOfficer}
            onChange={e => setDisp(v => ({ ...v, chiefSecurityOfficer: e.target.value }))}
            disabled={isLoading || isSaving}
            placeholder="Enter Chief Security Officer's name"
          />
          <div className="text-xs text-muted-foreground mt-1">Sets which user in the de.iterate platform is your Chief Security Officer (CSO).</div>
        </div>
        <div className="mt-6">
          <label className="block text-sm font-medium mb-1">Security Officer</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            value={disp.securityOfficer}
            onChange={e => setDisp(v => ({ ...v, securityOfficer: e.target.value }))}
            disabled={isLoading || isSaving}
            placeholder="Enter Security Officer's name"
          />
          <div className="text-xs text-muted-foreground mt-1">Sets which user in the de.iterate platform is your Security Officer.</div>
        </div>
        {/* <div>
          <label className="block text-sm font-medium mb-1">DISP Membership Number</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            value={disp.membershipNumber}
            onChange={e => setDisp(v => ({ ...v, membershipNumber: e.target.value }))}
            disabled={isLoading || isSaving}
            placeholder="Enter DISP membership number"
          />
        </div> */}
        {/* <div>
          <label className="block text-sm font-medium mb-1">DISP Expiry Date</label>
          <input
            type="date"
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            value={disp.expiryDate}
            onChange={e => setDisp(v => ({ ...v, expiryDate: e.target.value }))}
            disabled={isLoading || isSaving}
            placeholder="YYYY-MM-DD"
          />
        </div> */}
        {/* <div>
          <label className="block text-sm font-medium mb-1">Primary Contact</label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            value={disp.primaryContact}
            onChange={e => setDisp(v => ({ ...v, primaryContact: e.target.value }))}
            disabled={isLoading || isSaving}
            placeholder="Enter primary contact name"
          />
        </div> */}
        <div>
          <label className="block text-sm font-medium mb-1">Security Governance</label>
          <Select
            value={disp.securityGovernance}
            onValueChange={val => setDisp(v => ({ ...v, securityGovernance: val as DISPLevel }))}
            disabled={isLoading || isSaving}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              {DISP_LEVEL_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Personnel Security</label>
          <Select
            value={disp.personnelSecurity}
            onValueChange={val => setDisp(v => ({ ...v, personnelSecurity: val as DISPLevel }))}
            disabled={isLoading || isSaving}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              {DISP_LEVEL_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Physical Security</label>
          <Select
            value={disp.physicalSecurity}
            onValueChange={val => setDisp(v => ({ ...v, physicalSecurity: val as DISPLevel }))}
            disabled={isLoading || isSaving}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              {DISP_LEVEL_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Information/Cyber Security</label>
          <Select
            value={disp.informationCyberSecurity}
            onValueChange={val => setDisp(v => ({ ...v, informationCyberSecurity: val as DISPLevel }))}
            disabled={isLoading || isSaving}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select level" />
            </SelectTrigger>
            <SelectContent>
              {DISP_LEVEL_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!dispChanged || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save DISP Settings"}
        </Button>
      </div>
    </div>
  );
}

